import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, Loader2, ShieldCheck, Smartphone, KeyRound, Copy, CheckCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import client from '../api/client';
import '../styles/department-base.css';

export default function DepartmentLogin() {
  const navigate = useNavigate();

  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [setupToken, setSetupToken] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [savedCodes, setSavedCodes] = useState(false);

  const [totpToken, setTotpToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [isBackupCode, setIsBackupCode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  const totpInputRef = useRef(null);

  useEffect(() => {
    if ((step === 'totp_setup_verify' || step === 'totp_verify') && totpInputRef.current) {
      totpInputRef.current.focus();
    }
  }, [step, isBackupCode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await client.post('/api/department/auth/login', { email, password });
      const data = response.data;

      if (data.requiresTotpSetup) {
        setSetupToken(data.setupToken);
        await initTotpSetup(data.setupToken);
      } else if (data.requiresTotp) {
        setTotpToken(data.totpToken);
        setStep('totp_verify');
      } else if (data.token && data.account) {
        completeLogin(data.token, data.account);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const initTotpSetup = async (token) => {
    setLoading(true);
    try {
      const response = await client.post('/api/department/auth/totp/setup', { setupToken: token });
      const data = response.data;
      setQrCodeUrl(data.qrCodeUrl);
      setTotpSecret(data.secret);
      setBackupCodes(data.backupCodes);
      setSetupToken(data.setupToken);
      setStep('totp_setup');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initialize authenticator setup.');
      setStep('credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();
    if (!totpCode.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const response = await client.post('/api/department/auth/totp/verify-setup', {
        setupToken,
        code: totpCode.trim(),
        backupCodes,
      });
      const { token, account } = response.data;
      completeLogin(token, account);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code.');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async (e) => {
    e.preventDefault();
    if (!totpCode.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const response = await client.post('/api/department/auth/totp/verify', {
        totpToken,
        code: totpCode.trim(),
        isBackupCode,
      });
      const { token, account } = response.data;
      completeLogin(token, account);
    } catch (err) {
      const msg = err.response?.data?.message || 'Verification failed.';
      setError(msg);
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (token, account) => {
    localStorage.setItem('deptToken', token);
    localStorage.setItem('deptUser', JSON.stringify(account));
    client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    navigate('/departments/tickets');
  };

  const copyToClipboard = async (text, setCopied) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTotpCodeChange = (e) => {
    const val = e.target.value;
    if (isBackupCode) {
      if (/^[a-fA-F0-9]{0,8}$/.test(val)) setTotpCode(val);
    } else {
      if (/^\d{0,6}$/.test(val)) setTotpCode(val);
    }
  };

  const handleBackToLogin = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setLoading(false);
    setIsBackupCode(false);
    setTotpCode('');
    setTotpToken('');
    setSetupToken('');
    setQrCodeUrl('');
    setTotpSecret('');
    setBackupCodes([]);
    setSavedCodes(false);
    setCopiedSecret(false);
    setCopiedCodes(false);
    setError(null);
    setStep('credentials');
  };

  // Credential step
  if (step === 'credentials') {
    return (
      <div className="dept-login-container dept-login-container--credentials">
        <div className="dept-login-shell">
          <section className="dept-login-intro" aria-label="Department access information">
            <span className="dept-login-intro__badge">Secure Department Access</span>
            <h1>Welcome to the Department Access Portal</h1>
            <p>
              Sign in to manage department-assigned requests and operational workflows. Every login is protected with
              multi-step security controls for authorized personnel.
            </p>
            <ul className="dept-login-intro__highlights" aria-label="Security highlights">
              <li>
                <ShieldCheck size={18} />
                <span>Role-based access for authorized department accounts only.</span>
              </li>
              <li>
                <Smartphone size={18} />
                <span>Authenticator verification is enforced on sign-in.</span>
              </li>
              <li>
                <KeyRound size={18} />
                <span>Backup code recovery support for account continuity.</span>
              </li>
            </ul>
          </section>

          <section className="dept-login-form-column">
            <div className="dept-login-card dept-login-card--credentials">
              <div className="dept-login-header">
                <div className="dept-logo-circle">
                  <ShieldCheck size={30} color="#0b766e" />
                </div>
                <h2>Department Login</h2>
                <p>Secure access for department personnel only</p>
              </div>

              <form onSubmit={handleLogin} className="dept-login-form">
                {error && (
                  <div className="dept-alert dept-alert--error">
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="dept-form-group">
                  <label htmlFor="dept-email">Email Address</label>
                  <div className="dept-input-wrapper">
                    <User size={16} className="dept-input-icon" />
                    <input
                      id="dept-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@organization.com"
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <div className="dept-form-group">
                  <label htmlFor="dept-password">Password</label>
                  <div className="dept-input-wrapper">
                    <Lock size={16} className="dept-input-icon" />
                    <input
                      id="dept-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="dept-input-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="dept-btn dept-btn--primary dept-btn--full" disabled={loading}>
                  {loading ? (
                    <Loader2 size={18} className="dept-spin" />
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </section>
        </div>

        <footer className="dept-login-page-footer">
          <p>Need help? Contact your union representative</p>
          <p>Labor Connect - Luzon regional</p>
        </footer>
      </div>
    );
  }

  // TOTP Setup step
  if (step === 'totp_setup') {
    return (
      <div className="dept-login-container dept-login-container--totp">
        <div className="dept-login-shell dept-login-shell--totp dept-login-shell--setup">
          <section className="dept-login-intro dept-login-intro--totp" aria-label="Authenticator setup guidance">
            <span className="dept-login-intro__badge">Account Security Setup</span>
            <h1>Set Up Authenticator</h1>
            <p>
              Link your authenticator app to complete your department account protection. This setup is required once
              and helps keep operational access secure.
            </p>
            <ul className="dept-login-intro__highlights" aria-label="Setup reminders">
              <li>
                <Smartphone size={18} />
                <span>Scan the QR code using Google Authenticator, Microsoft Authenticator, or similar apps.</span>
              </li>
              <li>
                <KeyRound size={18} />
                <span>Save your backup codes before continuing to verification.</span>
              </li>
            </ul>
          </section>

          <section className="dept-login-form-column dept-login-form-column--setup">
            <div className="dept-login-card dept-login-card--totp dept-login-card--wide dept-login-card--totp-setup">
              <div className="dept-login-header">
                <div className="dept-logo-circle">
                  <Smartphone size={32} color="#0b766e" />
                </div>
                <h1>Set Up Authenticator</h1>
                <p>Scan the QR code with your Authenticator App</p>
              </div>

              <div className="dept-login-form">
                {error && (
                  <div className="dept-alert dept-alert--error">
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="dept-totp-setup dept-totp-setup--horizontal">
                  <div className="dept-totp-setup__left">
                    <div className="dept-totp-qr">
                      {qrCodeUrl && <img src={qrCodeUrl} alt="TOTP QR Code" />}
                    </div>

                    <div className="dept-totp-manual">
                      <p className="dept-totp-manual__label">Can't scan? Enter this key manually:</p>
                      <div className="dept-totp-manual__secret">
                        <code>{totpSecret}</code>
                        <button
                          type="button"
                          className="dept-totp-copy-btn"
                          onClick={() => copyToClipboard(totpSecret, setCopiedSecret)}
                          title="Copy secret"
                        >
                          {copiedSecret ? <CheckCircle size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="dept-totp-setup__right">
                    <div className="dept-totp-backup-section dept-totp-backup-section--setup">
                      <div className="dept-totp-backup-header">
                        <AlertTriangle size={16} />
                        <strong>Backup Codes</strong>
                      </div>
                      <p className="dept-totp-backup-desc">
                        Save these codes securely. Each can be used once if you lose access to your authenticator.
                      </p>
                      <div className="dept-backup-codes">
                        {backupCodes.map((code, i) => (
                          <code key={i} className="dept-backup-code">{code}</code>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="dept-btn dept-btn--soft dept-btn--full dept-btn--mt-sm"
                        onClick={() => copyToClipboard(backupCodes.join('\n'), setCopiedCodes)}
                      >
                        {copiedCodes ? <><CheckCircle size={16} /> Copied!</> : <><Copy size={16} /> Copy All Codes</>}
                      </button>
                    </div>

                    <label className="dept-totp-checkbox">
                      <input
                        type="checkbox"
                        checked={savedCodes}
                        onChange={(e) => setSavedCodes(e.target.checked)}
                      />
                      <span>I have saved my backup codes in a secure location</span>
                    </label>

                    <button
                      type="button"
                      className="dept-btn dept-btn--primary dept-btn--full"
                      disabled={!savedCodes || loading}
                      onClick={() => {
                        setTotpCode('');
                        setError(null);
                        setStep('totp_setup_verify');
                      }}
                    >
                      Continue to Verification
                    </button>

                    <button
                      type="button"
                      className="dept-btn dept-btn--soft dept-btn--full"
                      onClick={handleBackToLogin}
                    >
                      Back to Login Credentials
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <footer className="dept-login-page-footer">
          <p>Need help? Contact your union representative</p>
          <p>Labor Connect - Luzon regional</p>
        </footer>
      </div>
    );
  }

  // TOTP Setup Verify step
  if (step === 'totp_setup_verify') {
    return (
      <div className="dept-login-container dept-login-container--totp">
        <div className="dept-login-shell dept-login-shell--totp">
          <section className="dept-login-intro dept-login-intro--totp" aria-label="Authenticator verification guidance">
            <span className="dept-login-intro__badge">Final Setup Step</span>
            <h1>Verify Setup</h1>
            <p>
              Enter a fresh code from your authenticator app to confirm setup. Once verified, your department account
              is ready for secure sign-ins.
            </p>
            <ul className="dept-login-intro__highlights" aria-label="Verification reminders">
              <li>
                <KeyRound size={18} />
                <span>Use the latest 6-digit code from your authenticator app.</span>
              </li>
              <li>
                <ShieldCheck size={18} />
                <span>This confirms your device pairing and completes setup.</span>
              </li>
            </ul>
          </section>

          <section className="dept-login-form-column">
            <div className="dept-login-card dept-login-card--totp">
              <div className="dept-login-header">
                <div className="dept-logo-circle">
                  <KeyRound size={32} color="#0b766e" />
                </div>
                <h1>Verify Setup</h1>
                <p>Enter the 6-digit code from your authenticator app</p>
              </div>

              <form onSubmit={handleVerifySetup} className="dept-login-form">
                {error && (
                  <div className="dept-alert dept-alert--error">
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="dept-form-group">
                  <label htmlFor="dept-setup-totp-code">Verification Code</label>
                  <div className="dept-totp-input">
                    <input
                      ref={totpInputRef}
                      id="dept-setup-totp-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={handleTotpCodeChange}
                      placeholder="000000"
                      maxLength={6}
                    />
                  </div>
                </div>

                <button type="submit" className="dept-btn dept-btn--primary dept-btn--full" disabled={loading || totpCode.length < 6}>
                  {loading ? <Loader2 size={18} className="dept-spin" /> : 'Verify & Complete Setup'}
                </button>
              </form>
            </div>
          </section>
        </div>

        <footer className="dept-login-page-footer">
          <p>Need help? Contact your union representative</p>
          <p>Labor Connect - Luzon regional</p>
        </footer>
      </div>
    );
  }

  // TOTP Verify step (returning user)
  if (step === 'totp_verify') {
    return (
      <div className="dept-login-container dept-login-container--totp">
        <div className="dept-login-shell dept-login-shell--totp">
          <section className="dept-login-intro dept-login-intro--totp" aria-label="Two-factor security guidance">
            <span className="dept-login-intro__badge">Second Layer Verification</span>
            <h1>Two-Factor Authentication</h1>
            <p>
              Complete one more verification step to continue to the department portal. This extra layer keeps
              internal operations protected even if passwords are exposed.
            </p>
            <ul className="dept-login-intro__highlights" aria-label="Two-factor reminders">
              <li>
                <ShieldCheck size={18} />
                <span>Use your authenticator app for a fresh 6-digit code.</span>
              </li>
              <li>
                <KeyRound size={18} />
                <span>Backup codes can be used once each when your device is unavailable.</span>
              </li>
            </ul>
          </section>

          <section className="dept-login-form-column">
            <div className="dept-login-card dept-login-card--totp">
              <div className="dept-login-header">
                <div className="dept-logo-circle">
                  <KeyRound size={30} color="#0b766e" />
                </div>
                <h1>Two-Factor Authentication</h1>
                <p>{isBackupCode ? 'Enter one of your backup codes' : 'Enter the 6-digit code from your authenticator app'}</p>
              </div>

              <form onSubmit={handleVerifyTotp} className="dept-login-form">
                {error && (
                  <div className="dept-alert dept-alert--error">
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="dept-form-group">
                  <label htmlFor="dept-totp-code">{isBackupCode ? 'Backup Code' : 'Verification Code'}</label>
                  <div className="dept-totp-input">
                    <input
                      ref={totpInputRef}
                      id="dept-totp-code"
                      type="text"
                      inputMode={isBackupCode ? 'text' : 'numeric'}
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={handleTotpCodeChange}
                      placeholder={isBackupCode ? 'Enter backup code' : '000000'}
                      maxLength={isBackupCode ? 8 : 6}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="dept-btn dept-btn--primary dept-btn--full"
                  disabled={loading || totpCode.length < (isBackupCode ? 8 : 6)}
                >
                  {loading ? <Loader2 size={18} className="dept-spin" /> : 'Verify'}
                </button>

                <div className="dept-totp-toggle">
                  <button
                    type="button"
                    onClick={() => {
                      setIsBackupCode(!isBackupCode);
                      setTotpCode('');
                      setError(null);
                    }}
                  >
                    {isBackupCode ? 'Use authenticator code instead' : 'Use a backup code instead'}
                  </button>
                </div>

                <button type="button" className="dept-btn dept-btn--soft dept-btn--full" onClick={handleBackToLogin}>
                  Back to Login
                </button>
              </form>
            </div>
          </section>
        </div>

        <footer className="dept-login-page-footer">
          <p>Need help? Contact your union representative</p>
          <p>Labor Connect - Luzon regional</p>
        </footer>
      </div>
    );
  }

  return null;
}
